import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { 
  analyticsService, 
  RedemptionMetrics, 
  RedemptionTrend, 
  OfferRedemptionStats, 
  UserRedemptionEngagement 
} from '../../services/analyticsService';

import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import RedemptionKPICards from './components/RedemptionKPICards';
import RedemptionTrendChart from './components/RedemptionTrendChart';
import TopOffersTable from './components/TopOffersTable';
import UserEngagementLeaderboard from './components/UserEngagementLeaderboard';
import RedemptionHeatmap from './components/RedemptionHeatmap';
import RedemptionFilters from './components/RedemptionFilters';

const RedemptionAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Analytics data state
  const [redemptionMetrics, setRedemptionMetrics] = useState<RedemptionMetrics | null>(null);
  const [redemptionTrends, setRedemptionTrends] = useState<RedemptionTrend[]>([]);
  const [topOffers, setTopOffers] = useState<OfferRedemptionStats[]>([]);
  const [userEngagement, setUserEngagement] = useState<UserRedemptionEngagement[]>([]);
  
  // Filter states
  const [dateRange, setDateRange] = useState('30d');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('all');

  // Get date range for filters
  const getDateRange = (range: string) => {
    const endDate = new Date();
    const startDate = new Date();
    
    switch (range) {
      case '7d':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(endDate.getDate() - 90);
        break;
      case '1y':
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      default:
        startDate.setDate(endDate.getDate() - 30);
    }
    
    return { startDate, endDate };
  };

  // Load analytics data
  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { startDate, endDate } = getDateRange(dateRange);
      
      // Load all analytics data in parallel
      const [metrics, trends, offers, engagement] = await Promise.all([
        analyticsService.getRedemptionMetrics(),
        analyticsService.getRedemptionTrends(startDate, endDate),
        analyticsService.getTopOffersByRedemptions(),
        analyticsService.getUserEngagementLeaderboard(),
      ]);
      
      setRedemptionMetrics(metrics);
      setRedemptionTrends(trends);
      setTopOffers(offers);
      setUserEngagement(engagement);
    } catch (err) {
      console.error('Error loading redemption analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange, selectedCategory, selectedDistrict]);

  // Refresh data
  const handleRefresh = () => {
    loadAnalyticsData();
  };

  // Render content based on state
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading redemption analytics...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-destructive" />
            <div className="text-center mt-4">
              <h3 className="text-lg font-semibold text-foreground">Error Loading Analytics</h3>
              <p className="text-muted-foreground mt-2">{error}</p>
              <Button onClick={handleRefresh} className="mt-4">
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Redemption Analytics</h1>
            <p className="text-muted-foreground mt-2">
              Comprehensive redemption performance insights and revenue analysis
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={handleRefresh}>
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh
            </Button>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                Live Data
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <RedemptionFilters
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          selectedDistrict={selectedDistrict}
          onDistrictChange={setSelectedDistrict}
        />

        {/* KPI Cards */}
        {redemptionMetrics && (
          <RedemptionKPICards metrics={redemptionMetrics} />
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
          {/* Redemption Trends Chart */}
          <div className="xl:col-span-8">
            <RedemptionTrendChart 
              data={redemptionTrends} 
              title="Redemption Volume & Revenue Trends"
            />
          </div>

          {/* User Engagement Leaderboard */}
          <div className="xl:col-span-4">
            <UserEngagementLeaderboard 
              data={userEngagement} 
              title="Top Users by Savings"
            />
          </div>

          {/* Top Offers Table */}
          <div className="xl:col-span-8">
            <TopOffersTable 
              data={topOffers} 
              title="Top Performing Offers"
            />
          </div>

          {/* Redemption Heatmap */}
          <div className="xl:col-span-4">
            <RedemptionHeatmap 
              title="Peak Redemption Times"
            />
          </div>
        </div>

        {/* Analytics Insights */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Key Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="TrendingUp" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Revenue Growth</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Track revenue trends and identify peak performance periods
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Icon name="Users" size={20} className="text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">User Engagement</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Monitor user redemption patterns and savings achievements
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <Icon name="Calendar" size={20} className="text-orange-600" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Timing Insights</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Optimize offer timing based on redemption heatmap data
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Redemption Analytics - TravelHeroes Admin Dashboard</title>
        <meta name="description" content="Real-time redemption analytics and performance insights" />
      </Helmet>

      <div className="p-6">
        {renderContent()}
      </div>
    </>
  );
};

export default RedemptionAnalytics;